import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/atoms/card';
import { Button } from '@/components/atoms/button';
import { Badge } from '@/components/atoms/badge';
import { Alert, AlertDescription } from '@/components/atoms/alert';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertCircle, Clock, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import type { OnboardingStatus } from '../types/payout.types';
import { PayoutOnboardingFlow } from './payout-onboarding-flow';

interface OnboardingCardProps {
  status: OnboardingStatus;
  isOnboarded: boolean;
  requirements?: {
    currentlyDue: string[];
    eventuallyDue: string[];
    pastDue: string[];
  };
}

const statusConfig: Record<OnboardingStatus, {
  icon: React.ElementType;
  variant: 'default' | 'secondary' | 'outline' | 'destructive';
  color: string;
}> = {
  NOT_STARTED: { icon: AlertCircle, variant: 'secondary', color: 'text-muted-foreground' },
  PENDING: { icon: Clock, variant: 'outline', color: 'text-yellow-600' },
  REQUIRES_ACTION: { icon: AlertCircle, variant: 'destructive', color: 'text-orange-600' },
  COMPLETED: { icon: CheckCircle2, variant: 'default', color: 'text-green-600' },
  FAILED: { icon: AlertCircle, variant: 'destructive', color: 'text-red-600' },
};

export function OnboardingCard({ status, isOnboarded, requirements }: OnboardingCardProps) {
  const { t } = useTranslation();
  // Doc 51 Phase 5 — le CTA ouvre le parcours unifié (Popup 1 PSP → Popup 2 /
  // formulaire destination + MFA) au lieu d'appeler `startOnboarding` en direct.
  const [showOnboardingFlow, setShowOnboardingFlow] = useState(false);

  const config = statusConfig[status];
  const StatusIcon = config.icon;

  if (isOnboarded && status === 'COMPLETED') {
    return (
      <Card className="border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-800">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="size-5 text-green-600" />
            <CardTitle className="text-lg">{t('payouts.onboarding.completed')}</CardTitle>
          </div>
          <CardDescription>{t('payouts.onboarding.completedDescription')}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <StatusIcon className={`size-5 ${config.color}`} />
            <CardTitle className="text-lg">{t('payouts.onboarding.title')}</CardTitle>
          </div>
          <Badge variant={config.variant}>
            {t(`payouts.onboarding.status.${status}`)}
          </Badge>
        </div>
        <CardDescription>
          {t('payouts.onboarding.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {status === 'REQUIRES_ACTION' && requirements?.currentlyDue && requirements.currentlyDue.length > 0 && (
          <Alert variant="destructive">
            <AlertCircle className="size-4" />
            <AlertDescription>
              {t('payouts.onboarding.requiresAction')}
            </AlertDescription>
          </Alert>
        )}

        {status === 'PENDING' && (
          <Alert>
            <Clock className="size-4" />
            <AlertDescription>
              {t('payouts.onboarding.pendingReview')}
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <h4 className="font-medium text-sm">{t('payouts.onboarding.steps.title')}</h4>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-center gap-2">
              <CheckCircle2 className={`size-4 ${isOnboarded || status !== 'NOT_STARTED' ? 'text-green-600' : 'text-muted-foreground'}`} />
              {t('payouts.onboarding.steps.createAccount')}
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className={`size-4 ${isOnboarded ? 'text-green-600' : 'text-muted-foreground'}`} />
              {t('payouts.onboarding.steps.verifyIdentity')}
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className={`size-4 ${isOnboarded ? 'text-green-600' : 'text-muted-foreground'}`} />
              {t('payouts.onboarding.steps.addBankAccount')}
            </li>
          </ul>
        </div>

        {!isOnboarded && (
          <>
            <Button onClick={() => setShowOnboardingFlow(true)} className="w-full">
              <ExternalLink className="size-4 mr-2" />
              {status === 'NOT_STARTED'
                ? t('payouts.onboarding.startButton')
                : t('payouts.onboarding.continueButton')}
            </Button>
            <PayoutOnboardingFlow open={showOnboardingFlow} onOpenChange={setShowOnboardingFlow} />
          </>
        )}
      </CardContent>
    </Card>
  );
}
